import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {MenuService} from '../../service/menu.service';
import {WindowType} from '../../../modules/crud/domain/window-type';
import {AuthService} from '../../../infrastructure/auth/auth.service';
import {SidenavService} from '../../service/sidenav.service';
import {UserService} from 'src/app/infrastructure/auth/user.service';
import {UserTenant} from 'src/app/infrastructure/auth/user-tenant';
import {SwitchTenantComponent} from '../navbar/switch-tenant/switch-tenant.component';
import {MatDialog} from '@angular/material/dialog';
import {environment} from '../../../../environments/environment';
import {FeatureFlagsService} from '../../../infrastructure/service/feature-flags.service';
import {FEATURE_FLAG_KEYS} from '../../../infrastructure/domain/feature-flags';
import {LegalControlQueueService} from '../../../modules/legal/control-queue/service/legal-control-queue.service';
import {NAVIGATION_PAGES} from '../../domain/navigation-pages';

declare interface RouteInfo {
  path: string;
  title: string;
  icon: string;
  class: string;
}

declare interface AdminMenuItem {
  path: string;
  titleKey: string;
  icon: string;
  dataTourId: string;
}

@Component({
    selector: 'app-sidenav',
    templateUrl: './sidenav.component.html',
    styleUrls: ['./sidenav.component.scss'],
    standalone: false
})
export class SidenavComponent implements OnInit {
  readonly navigationPages = NAVIGATION_PAGES;

  @ViewChild('sidemenu')
  sidemenu!: ElementRef;

  windowType: typeof WindowType = WindowType;

  public menuItems!: any[];
  public isCollapsed = true;
  public manualUrl = environment.manualUrl;
  public readonly adminMenuItems: AdminMenuItem[] = [
    {
      path: '/analytics',
      titleKey: NAVIGATION_PAGES.analytics.titleKey,
      icon: NAVIGATION_PAGES.analytics.icon,
      dataTourId: 'menu-admin-analytics'
    },
    {
      path: '/support',
      titleKey: NAVIGATION_PAGES.support.titleKey,
      icon: NAVIGATION_PAGES.support.icon,
      dataTourId: 'menu-admin-support'
    },
    {
      path: '/settings/feature-flags',
      titleKey: NAVIGATION_PAGES.featureFlags.titleKey,
      icon: NAVIGATION_PAGES.featureFlags.icon,
      dataTourId: 'menu-admin-feature-flags'
    }
  ];

  isLimited = false;
  isAdmin = false;
  controlQueueMenuEnabled = false;
  controlQueuePendingReviewCount = 0;
  documentGeneratorMenuEnabled = false;

  userTenants!: UserTenant[];

  constructor(private router: Router,
              private service: MenuService,
              private authService: AuthService,
              private sidenavService: SidenavService,
              public userService: UserService,
              public dialog: MatDialog,
              private featureFlagsService: FeatureFlagsService,
              private controlQueueService: LegalControlQueueService) {
  }

  ngOnInit(): void {
    this.sidenavService.setMenuExpanded(!this.isCollapsed);

    this.authService.checkCredentials().then(auth => {
      if (auth) {
        this.service.getMenu().then(m => {
          const routes: RouteInfo[] = [];
          m.forEach(menu => {
            let windowPath = '/';
            switch (menu.type) {
              case this.windowType.ANGULAR:
                windowPath += menu.path;
                break;
              case this.windowType.FREE:
                windowPath += 'windows/' + menu.path;
                break;
              case this.windowType.CRUD:
                windowPath += 'crud/' + menu.path;
                break;
            }
            routes.push({path: windowPath, title: menu.name, icon: menu.icon, class: ''});
          });
          this.menuItems = routes;
        });

        this.userService.getUserTenants().then(ut => {
          this.userTenants = ut;
        });

        this.loadControlQueueMenuFlag();
        this.loadDocumentGeneratorMenuFlag();
      }
    });

    const userTenant = this.userService.loadLoggedUserTenant();

    if (userTenant) {
      this.isLimited = userTenant.limited;
      this.isAdmin = userTenant.user?.admin || false;
    }
  }

  close(event?: any): void {
    if (!event) {
      this.sidenavService.closeIgnorePreviousUrl();
    }

    if (this.isCollapsed || event?.target?.classList?.contains('menu-toggle')) {
      return;
    }

    this.isCollapsed = true;
    this.openOrCloseSidenav();
  }

  toggle(): void {
    this.isCollapsed = !this.isCollapsed;

    this.openOrCloseSidenav();
  }

  private openOrCloseSidenav(): void {
    if (this.isCollapsed) {
      this.sidemenu.nativeElement.classList.remove('opened');
    } else {
      this.sidemenu.nativeElement.classList.add('opened');
    }

    this.sidenavService.setMenuExpanded(!this.isCollapsed);
  }

  showTenantOptions(): void {
    this.close();
    const dialogRef = this.dialog.open(SwitchTenantComponent, {
      data: this.userTenants,
      // position: position,
      // height: '100vh'
    });
  }

  openManual(event?: Event): void {
    event?.preventDefault();
    this.close();

    if (this.manualUrl) {
      window.open(this.manualUrl, '_blank', 'noopener');
    }
  }

  get controlQueuePendingReviewCountLabel(): string {
    return this.controlQueuePendingReviewCount > 99
      ? '99+'
      : `${this.controlQueuePendingReviewCount}`;
  }

  private async loadControlQueueMenuFlag(): Promise<void> {
    try {
      this.controlQueueMenuEnabled = await this.featureFlagsService.isEnabled(FEATURE_FLAG_KEYS.LEGAL_CONTROL_QUEUE, false);
      if (!this.controlQueueMenuEnabled) {
        this.controlQueuePendingReviewCount = 0;
        return;
      }

      const counters = await this.controlQueueService.countByReviewStatus();
      this.controlQueuePendingReviewCount = Number(counters?.unread || 0) + Number(counters?.aiReviewed || 0);
    } catch {
      this.controlQueueMenuEnabled = false;
      this.controlQueuePendingReviewCount = 0;
    }
  }

  private async loadDocumentGeneratorMenuFlag(): Promise<void> {
    try {
      this.documentGeneratorMenuEnabled = await this.featureFlagsService.isEnabled(FEATURE_FLAG_KEYS.DOCUMENT_GENERATOR, false);
    } catch {
      this.documentGeneratorMenuEnabled = false;
    }
  }
}
